import { useCreateMutation } from '@gripp/shared-logic';
import {
  ControlledTextInput,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useAutoFocusRef,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';

const CREATE_PRODUCT = `
  mutation createProduct($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      name
      workspace {
        id
        name
      }
    }
  }
`;

type Props = {
  onComplete: () => void;
  workspaceId: string;
};

export const CreateProductForm = ({ workspaceId, onComplete }: Props) => {
  const { t } = useTranslation();
  const { mutateAsync, isPending } = useCreateMutation({
    modelName: 'product',
    query: CREATE_PRODUCT,
  });

  const formSchema = yup.object({
    name: yup.string().required(t('product.create.validation.name_required')),
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: { name: '' },
  });

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input = {
      name: data.name,
      workspace: { id: workspaceId },
    };
    await mutateAsync(
      { input },
      {
        onSettled: (data: any, error?: Error) => {
          if (error) return Promise.reject(error);
          onComplete();
        },
      }
    );
  };

  const elementFocusCallBack = useAutoFocusRef();

  return (
    <View style={styles.contentContainer}>
      <SaveFormTop
        isDirty={isDirty && !isPending}
        isValid={isValid && !isPending}
        onSubmit={handleSubmit(onSubmit)}
        title={t('product.create.label')}
        saveLabel={t('product.create.create')}
        topBar={TopModalBar}
        onCancel={onComplete}
      >
        <View style={styles.formItem}>
          <View>
            <Text style={styles.formItemTitle}>{t('product.list.name')}</Text>
          </View>
          <ControlledTextInput
            control={control}
            name="name"
            placeholder={t('product.create.placeholder')}
            innerRef={elementFocusCallBack}
          />
        </View>
      </SaveFormTop>
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    width: '100%',
  },
  header: {
    paddingVertical: 15,
    paddingHorizontal: 20,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingTop: Spacing.basePadding.paddingVertical,
  },
});
